import React, {useEffect, useState} from "react";
import {bool, func, number, string} from "prop-types";
import {AIcon} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {t} from "i18next";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import gfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import ReactMde from "react-mde";
import "react-mde/lib/styles/css/react-mde-all.css";
import {useIsMounted} from "hooks/useIsMounted";
import LinkTargetBlank from "./LinkTargetBlank";
import css from "./MarkdownEditor.module.scss";

const defaultShim = {
  commands: [],
  readOnly: true,
  classes: {
    toolbar: "invisible"
  }
};

const iconNames = {
  header: "textH",
  bold: "text-bolder",
  italic: "text-italic",
  strikethrough: "text-strikethrough",
  link: "link",
  quote: "quotes",
  code: "code",
  "unordered-list": "list-bullets",
  "ordered-list": "list-numbers",
  "checked-list": "list-checks"
};

export default function MarkdownEditor({
  active,
  disabled = false,
  hasValidationError = false,
  minEditorHeight = 400,
  minPreviewHeight = 100,
  isModal = false,
  onChange,
  usePlaceholderText,
  value
}) {
  const [selectedTab, setSelectedTab] = useState("preview");
  const hasMounted = useIsMounted().current;

  const getPhosphorIcons = (commandName) => (
    <AIcon size={20}>{iconNames[commandName]}</AIcon>
  );

  useEffect(() => {
    active ? setSelectedTab("write") : setSelectedTab("preview");
  }, [active]);

  return (
    <div
      className={classNames("markdown-editor", css.markdownEditor, {
        [css.active]: active,
        [css.modalHeight]: isModal
      })}>
      {!active && !value ? (
        <div>
          {usePlaceholderText && t("incidentDetail.detail.description.add")}
        </div>
      ) : (
        <ReactMde
          data-testid={"MarkdownEditor-ReactMde"}
          value={value}
          onChange={onChange}
          selectedTab={selectedTab}
          classes={{
            reactMde: css.reactMde,
            toolbar: css.mdeHeader,
            textArea: [
              disabled && css.disabled,
              hasValidationError && css.error
            ]
          }}
          onTabChange={setSelectedTab}
          getIcon={(commandName) => getPhosphorIcons(commandName)}
          generateMarkdownPreview={(markdown) =>
            Promise.resolve(
              <ReactMarkdown
                data-testid={"MarkdownEditor-ReactMarkdown"}
                rehypePlugins={[rehypeRaw, rehypeSanitize]}
                remarkPlugins={[gfm, remarkBreaks]}
                components={{
                  a: ({href, children}) => (
                    <LinkTargetBlank href={href}>{children}</LinkTargetBlank>
                  )
                }}>
                {markdown}
              </ReactMarkdown>
            )
          }
          minEditorHeight={minEditorHeight}
          minPreviewHeight={minPreviewHeight}
          heightUnits="px"
          toolbarCommands={[
            [
              "header",
              "bold",
              "italic",
              "strikethrough",
              "link",
              "quote",
              "code",
              "unordered-list",
              "ordered-list",
              "checked-list"
            ]
          ]}
          {...(active && hasMounted ? {readOnly: disabled} : defaultShim)}
        />
      )}
    </div>
  );
}

MarkdownEditor.propTypes = {
  active: bool.isRequired,
  /** Prevents interactions to the markdown editor and styles it accordingly */
  disabled: bool,
  hasValidationError: bool,
  /**
   * If true, modal height will be used
   *
   * @defaultValue false
   */
  isModal: bool,
  minEditorHeight: number,
  minPreviewHeight: number,
  onChange: func,
  usePlaceholderText: bool,
  value: string
};
